import { AxiosError, AxiosInstance, AxiosResponse } from 'axios';
import {
  InfiniteData,
  InvalidateQueryFilters,
  QueryFunctionContext,
  QueryKey,
  useInfiniteQuery,
  UseInfiniteQueryResult,
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { FetchOptions, MutationOptions, UseLoadMoreOptions } from './types';

/**
 * React Query builder class that provides a set of hooks for data fetching and mutations
 * with built-in TypeScript support and flexible configuration options.
 */
export class ReactQueryBuilder {
  /**
   * Creates a mutation function based on HTTP method
   */
  private createMutationFn = <Payload>(
    method: 'post' | 'patch' | 'put' | 'delete',
    url: string,
    options?: MutationOptions<Payload, any, any>,
  ) => {
    return (data: Payload) => {
      if (options?.transformRequest) {
        const { config, payload, requestUrl } = options.transformRequest(data);
        const targetUrl = requestUrl || url;

        switch (method) {
          case 'delete':
            return this.api.delete(targetUrl, config);
          case 'patch':
            return this.api.patch(targetUrl, payload || data, config);
          case 'post':
            return this.api.post(targetUrl, payload || data, config);
          case 'put':
            return this.api.put(targetUrl, payload || data, config);
        }
      }

      switch (method) {
        case 'delete':
          return this.api.delete(url);
        case 'patch':
          return this.api.patch(url, data);
        case 'post':
          return this.api.post(url, data);
        case 'put':
          return this.api.put(url, data);
      }
    };
  };

  private fetcher = <Response>(url: string, params?: unknown): Promise<Response> => {
    return this.api.get<Response>(url, { params }).then((res) => res.data);
  };

  /**
   * Generic mutation hook with flexible query key management
   */
  private useGenericMutation = <Payload, Response, TransformPayload = Payload>(
    url: string,
    mutationFn: (data: Payload) => Promise<AxiosResponse<Response>>,
    mutationOptions?: MutationOptions<Payload, Response, TransformPayload>,
  ): UseMutationResult<AxiosResponse<Response>, AxiosError, Payload> => {
    if (!url) {
      throw new Error('[Query] URL is required');
    }

    const queryClient = useQueryClient();
    const { optimisticUpdate, getInvalidateKeys, getOptimisticUpdateKey, ...restOptions } = mutationOptions || {};

    const getInvalidateQueryKeys = (
      response: AxiosResponse<Response> | undefined,
      payload: Payload,
    ): InvalidateQueryFilters<QueryKey>[] => {
      if (getInvalidateKeys) {
        return getInvalidateKeys(response, payload, url);
      }

      // Default: use transformed URL if available, otherwise original URL
      if (mutationOptions?.transformRequest) {
        const transformed = mutationOptions.transformRequest(payload);
        return [{ queryKey: this.createQueryKey(transformed.requestUrl || url) }];
      }

      return [{ queryKey: this.createQueryKey(url) }];
    };

    const getOptimisticQueryKey = (payload: Payload): QueryKey => {
      if (getOptimisticUpdateKey) {
        return getOptimisticUpdateKey(payload, url);
      }

      return this.createQueryKey(url);
    };

    return useMutation<AxiosResponse<Response>, AxiosError, Payload>({
      mutationFn,
      onSettled: (data, error, payload) => {
        const queryKeys = getInvalidateQueryKeys(data, payload);
        queryKeys.forEach((query) => {
          queryClient.invalidateQueries(query);
        });
      },
      onError: (err, payload, context) => {
        if (context) {
          const queryKey = getOptimisticQueryKey(payload);
          queryClient.setQueryData(queryKey, context);
        }
        throw err;
      },
      onMutate: async (payload) => {
        const queryKey = getOptimisticQueryKey(payload);
        await queryClient.cancelQueries({ queryKey });
        const previousData = queryClient.getQueryData<Response>(queryKey);

        if (optimisticUpdate) {
          queryClient.setQueryData<Response>(queryKey, (prev) => optimisticUpdate(prev, payload));
        }

        return previousData;
      },
      ...restOptions,
    });
  };

  useDelete = <Payload, Response, TransformPayload = Payload>(
    url: string,
    options?: MutationOptions<Payload, Response, TransformPayload>,
  ) => {
    return this.useGenericMutation<Payload, Response, TransformPayload>(
      url,
      this.createMutationFn('delete', url, options),
      options,
    );
  };

  useFetch = <Response, TransformResponse = Response>(
    url: string,
    config?: FetchOptions<Response, TransformResponse>,
  ) => {
    return useQuery<Response, AxiosError, TransformResponse, QueryKey>({
      enabled: !!url,
      queryKey: this.createQueryKey(url, config?.params),
      queryFn: () =>
        this.fetcher<Response>(url, config?.params).then((res) => {
          config?.onSuccess?.(res);
          return res;
        }),
      ...config,
    });
  };

  useLoadMore = <Page, MappedData = InfiniteData<Page, number>>(
    url: string,
    options: UseLoadMoreOptions<Page, MappedData>,
  ): UseInfiniteQueryResult<MappedData, AxiosError> => {
    if (!url) {
      throw new Error('[Query] URL is required');
    }

    const baseParams = options.transformParams(1);
    const { page, ...staticParams } = baseParams;
    const queryKey = this.createQueryKey(url, options.includeParamsToQueryKey ? staticParams : undefined);

    return useInfiniteQuery<Page, AxiosError, MappedData, QueryKey, number>({
      ...options,
      queryKey,
      enabled: !!url,
      initialPageParam: 1,
      queryFn: async ({ pageParam }: QueryFunctionContext<QueryKey, number>) => {
        const response = await this.api.get<Page>(url, {
          params: options.transformParams(pageParam),
        });
        options?.onSuccess?.(response.data);
        return response.data;
      },
    });
  };

  usePatch = <Payload, Response, TransformPayload = Payload>(
    url: string,
    options?: MutationOptions<Payload, Response, TransformPayload>,
  ) => {
    return this.useGenericMutation<Payload, Response, TransformPayload>(
      url,
      this.createMutationFn('patch', url, options),
      options,
    );
  };

  // Mutation methods using the consolidated logic
  usePost = <Payload, Response, TransformPayload = Payload>(
    url: string,
    options?: MutationOptions<Payload, Response, TransformPayload>,
  ) => {
    return this.useGenericMutation<Payload, Response, TransformPayload>(
      url,
      this.createMutationFn('post', url, options),
      options,
    );
  };

  usePrefetch = <Response>(url: string, options?: FetchOptions<Response>) => {
    const queryClient = useQueryClient();
    return queryClient.prefetchQuery({
      queryKey: this.createQueryKey(url),
      queryFn: () => this.fetcher<Response>(url),
      ...options,
    });
  };

  useUpdate = <Payload, Response, TransformPayload = Payload>(
    url: string,
    options?: MutationOptions<Payload, Response, TransformPayload>,
  ) => {
    return this.useGenericMutation<Payload, Response, TransformPayload>(
      url,
      this.createMutationFn('patch', url, options),
      options,
    );
  };

  constructor(private readonly api: AxiosInstance) {}

  /**
   * Public helper method to create query keys
   * Useful when defining custom getInvalidateKeys or getOptimisticUpdateKey functions
   */
  createQueryKey(url: string, params?: Record<string, unknown>): QueryKey {
    if (params && Object.keys(params).length > 0) {
      return [url, ...Object.entries(params).map(([k, v]) => `${k}=${v}`)] as const;
    }
    return [url] as const;
  }
}
